import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NeedsAttentionItem } from '../Home/map/needsAttention';
import {
  advanceRotation,
  describeStep,
  jumpToShipment,
  overviewState,
  RotationState,
  RotationStep,
  buildLoopQueue,
  stepDurationMs,
} from './monitorRotation';

const TICK_MS = 1_000;

export interface MonitorRotation {
  step: RotationStep;
  /** What this loop spotlights (see buildLoopQueue). */
  queue: NeedsAttentionItem[];
  /** 0–1 through the current step's dwell. */
  progress: number;
  secondsLeft: number;
  jumpTo: (item: NeedsAttentionItem) => void;
  /** The rotation is held on the current step (see togglePause). */
  paused: boolean;
  /**
   * Holds the rotation on the current step, or lets it carry on with the
   * time that step had left. Jumping to an item while paused stays paused,
   * on that item. Only the rotation stops: the data keeps refreshing.
   */
  togglePause: () => void;
}

export const useMonitorRotation = (
  items: NeedsAttentionItem[],
  arriving: NeedsAttentionItem[] = [],
  pickups: NeedsAttentionItem[] = []
): MonitorRotation => {
  const [state, setStateValue] = useState<RotationState>(() =>
    overviewState(Date.now())
  );
  const [now, setNow] = useState(() => Date.now());
  // Each return to the overview starts the next loop (the next page).
  const [loop, setLoop] = useState(0);
  const queue = useMemo(
    () => buildLoopQueue(items, loop, undefined, arriving, pickups),
    [items, loop, arriving, pickups]
  );
  // Read inside the interval so a queue rebuilt on every stream flush does
  // not restart the timer.
  const queueRef = useRef(queue);
  queueRef.current = queue;
  // When the rotation was paused; null while it runs.
  const [pausedAt, setPausedAt] = useState<number | null>(null);
  const pausedAtRef = useRef(pausedAt);
  pausedAtRef.current = pausedAt;
  const stateRef = useRef(state);
  const setState = useCallback((next: RotationState) => {
    stateRef.current = next;
    setStateValue(next);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      // Paused: the step and its countdown hold where they are.
      if (pausedAtRef.current !== null) return;
      const tick = Date.now();
      setNow(tick);
      const current = stateRef.current;
      if (tick - current.startedAt < stepDurationMs(current)) return;
      const next = advanceRotation(current, queueRef.current, tick);
      if (current.shipmentId !== null && next.shipmentId === null) {
        setLoop((n) => n + 1);
      }
      setState(next);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [setState]);

  const jumpTo = useCallback(
    (item: NeedsAttentionItem) => {
      const tick = Date.now();
      setNow(tick);
      // Paused, the item it jumps to holds with its full time.
      if (pausedAtRef.current !== null) setPausedAt(tick);
      setState(
        jumpToShipment(
          item,
          tick,
          queueRef.current.findIndex(
            (queued) => String(queued.shipmentId) === String(item.shipmentId)
          )
        )
      );
    },
    [setState]
  );

  const togglePause = useCallback(() => {
    const tick = Date.now();
    const since = pausedAtRef.current;
    if (since === null) {
      setPausedAt(tick);
      return;
    }
    // Resume with the time the step had left: move its start on by the
    // time spent paused.
    const current = stateRef.current;
    setState({ ...current, startedAt: current.startedAt + (tick - since) });
    setNow(tick);
    setPausedAt(null);
  }, [setState]);

  const step = useMemo(() => describeStep(state, queue), [state, queue]);
  const duration = stepDurationMs(state);
  // Paused, the countdown reads as it was at the pause.
  const at = pausedAt ?? now;
  const elapsed = Math.min(Math.max(at - state.startedAt, 0), duration);

  return {
    step,
    queue,
    progress: elapsed / duration,
    secondsLeft: Math.ceil((duration - elapsed) / 1000),
    jumpTo,
    paused: pausedAt !== null,
    togglePause,
  };
};
